import { useCallback, useState } from "react";
import { type ApiError, OpenAPI } from "../services/intranet-portal-services";

import { selectToken } from "../views/utilities/auth/authSlice";
import { toast } from "react-toastify";
import { runTimeConfig } from "@/config";
import {
  getApiErrorMessage,
  handleSessionExpiry,
  isApiError,
  isSessionExpired
} from "@/utils/apiErrorHandler";
import { refreshSession } from "@/services/authRefresh";
import { authService } from "@/services/authServiceInstance";

export interface UseIntranetPortalCoreApiReturn {
  handleRequest: <T>(requestFn: () => Promise<T>) => Promise<T | undefined>;
  isLoading: boolean;
  error: ApiError | undefined;
  dismissError: () => void;
}

const useIntranetPortalCoreApi = (): UseIntranetPortalCoreApiReturn => {
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Ensure BASE is set (fallback to empty string if missing)
  const baseUrl = runTimeConfig?.VITE_BASE_URL ?? "";
  if (OpenAPI.BASE !== baseUrl) {
    OpenAPI.BASE = baseUrl;
  }

  // Use a resolver function so every request gets the latest token from localStorage
  OpenAPI.TOKEN = async () => {
    const token = selectToken();
    return typeof token === "string" ? token : "";
  };

  const handleRequest = useCallback(
    async <T,>(requestFn: () => Promise<T>): Promise<T | undefined> => {
      setIsLoading(true);

      try {
        const response = await requestFn();
        return response;
      } catch (exception: unknown) {
        if (!isApiError(exception)) {
          toast.error("Network error. Please check your connection.");
          return undefined;
        }

        // TOKEN EXPIRED or UNAUTHORIZED
        if (exception.status === 401 || isSessionExpired(exception)) {
          try {
            // Attempt to refresh the session
            await refreshSession();

            // Retry the request with the new session
            // The OpenAPI.TOKEN resolver will automatically pick up the new token
            return await requestFn();
          } catch (refreshErr) {
            console.error("Session Refresh Failed:", refreshErr);
            handleSessionExpiry(() => {
              authService.localLogout();
            });
            return undefined;
          }
        }

        // Handle other API errors
        const errorMessage = getApiErrorMessage(exception);
        if (errorMessage != null && errorMessage !== "") {
          toast.error(errorMessage);
        }

        setError(exception);
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    handleRequest,
    isLoading,
    error,
    dismissError: () => {
      setError(undefined);
    }
  };
};

export default useIntranetPortalCoreApi;
